import { useEffect, useRef, useMemo } from "react"
import { router } from "@inertiajs/react"
import cable from "@/lib/cable"

const POLL_INTERVAL_MS = 5000
const RELOAD_DEBOUNCE_MS = 500

type SocialPostEvent = {
  event: "publish_progress"
  platform: string
  status: string
  detail?: string
  timestamp: string
}

/**
 * Subscribes to SocialPostChannel for real-time status updates and polls as
 * a fallback while any social post is in-flight (pending/uploading).
 *
 * Triggers `router.reload()` to refresh Inertia props on every status change.
 */
export function useSocialPostChannel(
  renderIds: string[],
  hasInflightPosts: boolean,
) {
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Stable key so the effect only re-runs when the actual IDs change
  const renderKey = useMemo(() => [...renderIds].sort().join(","), [renderIds])

  function debouncedReload() {
    if (reloadTimer.current) return
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null
      router.reload()
    }, RELOAD_DEBOUNCE_MS)
  }

  // ActionCable subscriptions — one per render with in-flight posts
  useEffect(() => {
    if (!renderKey) return

    const ids = renderKey.split(",").filter(Boolean)
    const subscriptions = ids.map((renderId) =>
      cable.subscriptions.create(
        { channel: "SocialPostChannel", render_id: renderId },
        {
          received(data: SocialPostEvent) {
            if (data.event === "publish_progress") {
              debouncedReload()
            }
          },
        },
      ),
    )

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe())
      if (reloadTimer.current) {
        clearTimeout(reloadTimer.current)
        reloadTimer.current = null
      }
    }
  }, [renderKey])

  // Polling fallback — catches state changes if WebSocket drops
  useEffect(() => {
    if (!hasInflightPosts) return

    const interval = setInterval(() => {
      router.reload()
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [hasInflightPosts])
}
